import React from 'react'
import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded'
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded'
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded'
import MicRoundedIcon from '@mui/icons-material/MicRounded'
import ScreenShareRoundedIcon from '@mui/icons-material/ScreenShareRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import StopScreenShareRoundedIcon from '@mui/icons-material/StopScreenShareRounded'
import VideocamOffRoundedIcon from '@mui/icons-material/VideocamOffRounded'
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded'

void React

function renderSelectOptions(options = []) {
  return options.map((option) => (
    <option key={option.id} value={option.id}>
      {option.label}
    </option>
  ))
}

function resolveSelectValue(selectedValue) {
  if (typeof selectedValue === 'string' && selectedValue.trim()) {
    return selectedValue
  }

  return ''
}

function findOptionById(options = [], optionId) {
  return options.find((option) => option?.id === optionId) || null
}

export default function CallControlsOverlay({
  audioEnabled = true,
  videoEnabled = true,
  layoutId,
  railStateId,
  videoLayoutOptions = [],
  railStateOptions = [],
  onLayoutChange,
  onRailStateChange,
  onToggleAudio,
  onToggleVideo,
  onLeave,
  screenShareActive = false,
  onToggleScreenShare,
  showScreenShareButton = false,
  disableScreenShareButton = false,
  layoutMenuOpen = false,
  onToggleLayoutMenu,
  deviceSettingsOpen = false,
  onToggleDeviceSettings,
  showDeviceSettingsButton = true,
  cameraDevices = [],
  microphoneDevices = [],
  speakerDevices = [],
  selectedCameraDeviceId = '',
  selectedMicrophoneDeviceId = '',
  selectedSpeakerDeviceId = '',
  onCameraDeviceChange,
  onMicrophoneDeviceChange,
  onSpeakerDeviceChange,
  showLayoutSelector = true,
  showRailSelector = false,
  showAudioButton = true,
  showVideoButton = true,
  showLeaveButton = true,
}) {
  const currentLayoutOption = findOptionById(videoLayoutOptions, layoutId)
  const currentRailStateOption = findOptionById(railStateOptions, railStateId)
  const showLayoutMenuButton = showLayoutSelector || showRailSelector

  return (
    <div
      className="shared-video-controls"
      onMouseDown={(event) => {
        event.stopPropagation()
      }}
      onTouchStart={(event) => {
        event.stopPropagation()
      }}
    >
      {layoutMenuOpen ? (
        <div className="shared-video-controls__panel" role="dialog" aria-label="Layout options">
          {showLayoutSelector ? (
            <div className="shared-video-controls__group">
              <span className="shared-video-controls__group-title">Layout</span>
              <div className="shared-video-controls__option-grid">
                {videoLayoutOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`shared-video-controls__option${
                      option.id === layoutId ? ' shared-video-controls__option--active' : ''
                    }`}
                    onClick={() => onLayoutChange?.(option.id)}
                    aria-pressed={option.id === layoutId}
                  >
                    <span className="shared-video-controls__option-title">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {showRailSelector ? (
            <div className="shared-video-controls__group">
              <span className="shared-video-controls__group-title">Participant rail</span>
              <div className="shared-video-controls__option-grid shared-video-controls__option-grid--compact">
                {railStateOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`shared-video-controls__option${
                      option.id === railStateId ? ' shared-video-controls__option--active' : ''
                    }`}
                    onClick={() => onRailStateChange?.(option.id)}
                    aria-pressed={option.id === railStateId}
                  >
                    <span className="shared-video-controls__option-title">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {deviceSettingsOpen ? (
        <div className="shared-video-controls__panel" role="dialog" aria-label="Device settings">
          <label className="shared-video-controls__field">
            <span>Microphone</span>
            <select
              aria-label="Microphone device"
              value={resolveSelectValue(selectedMicrophoneDeviceId)}
              onChange={(event) => onMicrophoneDeviceChange?.(event.target.value)}
            >
              <option value="">System default</option>
              {renderSelectOptions(microphoneDevices)}
            </select>
          </label>

          <label className="shared-video-controls__field">
            <span>Camera</span>
            <select
              aria-label="Camera device"
              value={resolveSelectValue(selectedCameraDeviceId)}
              onChange={(event) => onCameraDeviceChange?.(event.target.value)}
            >
              <option value="">System default</option>
              {renderSelectOptions(cameraDevices)}
            </select>
          </label>

          <label className="shared-video-controls__field">
            <span>Speaker</span>
            <select
              aria-label="Speaker device"
              value={resolveSelectValue(selectedSpeakerDeviceId)}
              onChange={(event) => onSpeakerDeviceChange?.(event.target.value)}
            >
              <option value="">System default</option>
              {renderSelectOptions(speakerDevices)}
            </select>
          </label>
        </div>
      ) : null}

      {showAudioButton ? (
        <button
          type="button"
          className={`shared-video-controls__button${
            audioEnabled ? '' : ' shared-video-controls__button--muted'
          }`}
          onClick={onToggleAudio}
          aria-label={audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
        >
          {audioEnabled ? <MicRoundedIcon sx={{ fontSize: 20 }} /> : <MicOffRoundedIcon sx={{ fontSize: 20 }} />}
        </button>
      ) : null}

      {showVideoButton ? (
        <button
          type="button"
          className={`shared-video-controls__button${
            videoEnabled ? '' : ' shared-video-controls__button--muted'
          }`}
          onClick={onToggleVideo}
          aria-label={videoEnabled ? 'Turn camera off' : 'Turn camera on'}
        >
          {videoEnabled ? (
            <VideocamRoundedIcon sx={{ fontSize: 20 }} />
          ) : (
            <VideocamOffRoundedIcon sx={{ fontSize: 20 }} />
          )}
        </button>
      ) : null}

      {showLayoutMenuButton ? (
        <button
          type="button"
          className={`shared-video-controls__button shared-video-controls__button--labeled${
            layoutMenuOpen ? ' shared-video-controls__button--active' : ''
          }`}
          onClick={onToggleLayoutMenu}
          aria-label={layoutMenuOpen ? 'Close layout options' : 'Open layout options'}
          aria-expanded={layoutMenuOpen}
        >
          <DashboardRoundedIcon sx={{ fontSize: 20 }} />
          <span className="shared-video-controls__button-label">
            {currentLayoutOption?.label || 'Layout'}
          </span>
          {showRailSelector && currentRailStateOption?.label ? (
            <span className="shared-video-controls__button-subtitle">
              {currentRailStateOption.label}
            </span>
          ) : null}
        </button>
      ) : null}

      {showScreenShareButton ? (
        <button
          type="button"
          className={`shared-video-controls__button shared-video-controls__button--labeled${
            screenShareActive
              ? ' shared-video-controls__button--sharing'
              : ''
          }`}
          onClick={onToggleScreenShare}
          aria-label={screenShareActive ? 'Stop screen sharing' : 'Share screen'}
          aria-pressed={screenShareActive}
          disabled={disableScreenShareButton}
        >
          {screenShareActive ? (
            <StopScreenShareRoundedIcon sx={{ fontSize: 20 }} />
          ) : (
            <ScreenShareRoundedIcon sx={{ fontSize: 20 }} />
          )}
          <span className="shared-video-controls__button-label">
            {screenShareActive ? 'Stop share' : 'Share screen'}
          </span>
        </button>
      ) : null}

      {showDeviceSettingsButton ? (
        <button
          type="button"
          className={`shared-video-controls__button${
            deviceSettingsOpen ? ' shared-video-controls__button--active' : ''
          }`}
          onClick={onToggleDeviceSettings}
          aria-label={deviceSettingsOpen ? 'Close device settings' : 'Open device settings'}
          aria-expanded={deviceSettingsOpen}
        >
          <SettingsRoundedIcon sx={{ fontSize: 20 }} />
        </button>
      ) : null}

      {showLeaveButton ? (
        <button
          type="button"
          className="shared-video-controls__button shared-video-controls__button--danger"
          onClick={onLeave}
          aria-label="Leave call"
        >
          <CallEndRoundedIcon sx={{ fontSize: 20 }} />
        </button>
      ) : null}
    </div>
  )
}

CallControlsOverlay.displayName = 'CallControlsOverlay'
